import { useCallback } from "react";

import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { LookThroughStatusPrompt } from "components/Prompts/LookThroughStatusPrompt";
import { useNavigate } from "hooks";
import { useModals, usePrompt, useRoleGuardHook } from "hooks";
import { useDealActions, useTaskManageActions, useTokenSelector, useViewActions } from "store";
import { IFile, IItemToShare } from "store/types";

import { EntityType, FileType, RoleName } from "../../../constants";
import { FileInfoPrompt } from "../../Prompts/FileInfoPrompt";
import { useWorkspace } from "../WorkspaceContext";

export interface UseWorkspaceFileHookParams {
  file: {
    id: number;
    name: string;
    type: FileType;
    created_at: string;
    section_id: number | null;
    deal_id: number | null;
  };
  refetchHandler?: () => Promise<void>;
}

export const useWorkspaceFileHook = ({ file, refetchHandler }: UseWorkspaceFileHookParams) => {
  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const { deleteModal } = useModals();
  const token = useTokenSelector();
  const { dispatchDetail } = useWorkspace();

  const { setShareDialog } = useViewActions();
  const { setCreateAction } = useTaskManageActions();
  const {
    downloadFileAction,
    createInvestmentDetailFromAIAction,
    getFileMetaAction,
    deleteFileAction,
    extractFileRepoK1Action
  } = useDealActions();

  const handleShare = useCallback(() => {
    setShareDialog({
      elements: [file.id],
      type: "file_id",
      itemToShare: file as unknown as IItemToShare,
      shareName: file.name,
      dealId: file.deal_id ?? null,
      cb: async () => {
        await refetchHandler?.();
      }
    });
  }, [setShareDialog, file, refetchHandler]);

  const handleDownload = useCallback(() => {
    downloadFileAction({ fileId: file.id, fileName: file.name });
  }, [downloadFileAction, file]);

  const handleDelete = useCallback(() => {
    return deleteModal({
      element: "Delete item permanently?",
      message: "This cannot be undone",
      onPressSuccess: "Delete permanently",
      action: async () => {
        await deleteFileAction({ ...file } as IFile);
        dispatchDetail({ key: "delete", id: file.id, entityType: EntityType.FILE });
      }
    });
  }, [deleteModal, file, deleteFileAction, refetchHandler]);

  const handleAddTask = useCallback(() => {
    setCreateAction({
      isOpen: true,
      entity_id: file.id,
      entity_type: "file"
    });
  }, [setCreateAction, file.id]);

  const handleDataExtraction = useCallback(() => {
    openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds: [file.id]
      }
    });
  }, [openPrompt, file.id]);

  const handleK1Extract = useCallback(async () => {
    await extractFileRepoK1Action([file.id]);
  }, [extractFileRepoK1Action, file.id]);

  const handleLookThrough = useCallback(() => {
    if (!file.deal_id) return;
    const dealId = file.deal_id;
    const promise = createInvestmentDetailFromAIAction({
      file_ids: [file.id],
      files: [],
      dealId
    });
    //need to handle error before opening the prompt
    promise.catch(() => {});
    openPrompt({
      componentType: "status",
      Component: LookThroughStatusPrompt,
      statusTitle: "Look Through extraction",
      props: {
        promise,
        dealId
      }
    });
  }, [createInvestmentDetailFromAIAction, file, openPrompt]);

  const onAddValueFromPDF = useCallback(() => {
    if (!file.deal_id) return;
    if (file.section_id == null) return;
    if (token && file.type === FileType["PDF"]) {
      try {
        navigate({
          route: "tagger",
          search: {
            dealId: file.deal_id,
            filesIds: [file.id],
            section: file.section_id
          },
          setQueriesMethod: "replace"
        });
      } catch (error) {
        throw error;
      }
    }
  }, [file]);

  const handleShareWithRole = useRoleGuardHook(handleShare, [RoleName.Admin, RoleName.Analyst]);
  const handleDeleteWithRole = useRoleGuardHook(handleDelete, [RoleName.Admin, RoleName.Analyst]);

  const handleAddTaskWithRole = useRoleGuardHook(handleAddTask, [RoleName.Admin, RoleName.Analyst]);
  const handleDataExtractionWithRole = useRoleGuardHook(handleDataExtraction, [RoleName.Admin, RoleName.Analyst]);
  const handleK1ExtractWithRole = useRoleGuardHook(handleK1Extract, [RoleName.Admin, RoleName.Analyst]);
  const handleLookThroughWithRole = useRoleGuardHook(handleLookThrough, [RoleName.Admin, RoleName.Analyst]);
  const handleAddValueFromPDFWithRole = useRoleGuardHook(onAddValueFromPDF, [RoleName.Admin, RoleName.Analyst]);
  const handleDownloadFileWithRole = useRoleGuardHook(handleDownload, [
    RoleName.Admin,
    RoleName.Analyst,
    RoleName.Guest,
    RoleName.Viewer,
    RoleName.TeamViewer
  ]);

  const onFileInfo = useCallback(() => {
    if (!file.deal_id) return undefined;
    getFileMetaAction(file.deal_id, file.id);
    openPrompt({
      Component: FileInfoPrompt,
      props: {
        file: { name: file.name, type: file.type, created_at: file.created_at },
        onShare: handleShareWithRole,
        onDownload: handleDownload
      }
    });
  }, [file, handleShareWithRole, openPrompt]);

  const onFileInfoWithRole = useRoleGuardHook(onFileInfo, [
    RoleName.Admin,
    RoleName.Analyst,
    RoleName.Guest,
    RoleName.Viewer,
    RoleName.TeamViewer
  ]);
  const handleFileInfoWithRole = file.deal_id ? onFileInfoWithRole : undefined;

  return {
    handleShare: handleShareWithRole,
    handleDelete: handleDeleteWithRole,
    handleDownload: handleDownloadFileWithRole,
    handleAddTask: handleAddTaskWithRole,
    handleDataExtraction: handleDataExtractionWithRole,
    handleExtractK1: handleK1ExtractWithRole,
    handleLookThrough: handleLookThroughWithRole,
    handleFileInfo: handleFileInfoWithRole,
    handleAddValueFromPDF: handleAddValueFromPDFWithRole
  };
};
